import React, { useEffect, useMemo, useState } from 'react'

import { ApiResponse } from '../core/types'
import Loader from './loader'
import { CloseIcon } from './closeIcon'
import { CopyIcon } from './copyIcon'

type Format = 'json' | 'xml' | 'form' | 'plain' | 'empty' | 'unknown'

interface SelectedPayload {
  label: string
  payload: string
  hintedFormat?: string
}

interface PreparedPayload {
  format: Format
  display: string
  error: string | null
}

const normalizeFormat = (format?: string | null): Format => {
  if (!format) return 'unknown'

  switch (format.toLowerCase()) {
    case 'json':
      return 'json'
    case 'xml':
      return 'xml'
    case 'form':
    case 'form-encoded':
    case 'x-www-form-urlencoded':
      return 'form'
    case 'plain':
    case 'text':
    case 'text/plain':
      return 'plain'
    default:
      return 'unknown'
  }
}

const isJsonString = (payload: string): boolean => {
  if (!payload) return false
  const trimmed = payload.trim()
  if (!trimmed) return false
  if (!['{', '['].includes(trimmed[0])) return false

  try {
    JSON.parse(trimmed)
    return true
  } catch (_error) {
    return false
  }
}

const isLikelyXml = (payload: string): boolean => {
  if (!payload) return false
  const trimmed = payload.trim()
  return trimmed.startsWith('<')
}

const isFormEncoded = (payload: string): boolean => {
  if (!payload) return false
  const trimmed = payload.trim()
  if (!trimmed.includes('=')) return false

  const pairs = trimmed.split('&')
  if (pairs.length <= 1) return false

  return pairs.every((pair) => pair.includes('='))
}

const detectFormatFromPayload = (payload?: string): Format => {
  if (!payload) return 'empty'
  if (isJsonString(payload)) return 'json'
  if (isLikelyXml(payload)) return 'xml'
  if (isFormEncoded(payload)) return 'form'
  return 'plain'
}

const resolveFormat = (payload: string, hintedFormat?: string): Format => {
  const normalizedHint = normalizeFormat(hintedFormat)
  if (normalizedHint !== 'unknown') {
    return normalizedHint
  }

  return detectFormatFromPayload(payload)
}

const formatJson = (payload: string): { formatted: string; ok: boolean } => {
  try {
    const parsed = JSON.parse(payload)
    return { formatted: JSON.stringify(parsed, null, 2), ok: true }
  } catch (_error) {
    return { formatted: payload, ok: false }
  }
}

const formatFormEncoded = (payload: string): string => {
  return payload
    .split('&')
    .map((pair) => {
      const [rawKey, rawValue = ''] = pair.split('=')
      try {
        const key = decodeURIComponent(rawKey.replace(/\+/g, ' '))
        const value = decodeURIComponent(rawValue.replace(/\+/g, ' '))
        return `${key}: ${value}`
      } catch (_error) {
        return pair
      }
    })
    .join('\n')
}

const formatXml = (payload: string): string => {
  try {
    const trimmed = payload.trim()
    if (!trimmed) return payload

    const sanitized = trimmed.replace(/\r\n|\r/g, '\n')
    const withBreaks = sanitized.replace(/>(\s*)</g, '>$1\n<')
    const lines = withBreaks.split('\n').map((line) => line.trim()).filter(Boolean)
    const pad = '  '
    let indent = 0
    const formatted: string[] = []

    lines.forEach((line) => {
      const isClosingTag = /^<\//.test(line)
      const isOpeningTag = /^<[^!?\s\/][^>]*?>$/.test(line)
      const isSelfClosing = /<[^>]+\/>$/.test(line)

      if (isClosingTag) {
        indent = Math.max(indent - 1, 0)
      }

      formatted.push(`${pad.repeat(indent)}${line}`)

      if (isOpeningTag && !isSelfClosing && !isClosingTag) {
        indent += 1
      }
    })

    return formatted.join('\n')
  } catch (_error) {
    return payload
  }
}

const formatLabel = (format: Format): string => {
  switch (format) {
    case 'json':
      return 'JSON'
    case 'xml':
      return 'XML'
    case 'form':
      return 'Form'
    case 'plain':
      return 'Plain Text'
    case 'empty':
      return 'Empty'
    default:
      return 'Unknown'
  }
}

interface Props {
  apiResponses: ApiResponse[]
}

export const ApiReponses = ({ apiResponses }: Props) => {
  const [selected, setSelected] = useState<SelectedPayload | null>(null)
  const [preparedPayload, setPreparedPayload] = useState<PreparedPayload>({
    format: 'empty',
    display: '',
    error: null,
  })
  const [isPreparing, setIsPreparing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (!selected || !selected.payload) {
      setPreparedPayload({ format: 'empty', display: '', error: null })
      setIsPreparing(false)
      setSearchTerm('')
      return
    }

    setIsPreparing(true)
    setSearchTerm('')

    const job = window.setTimeout(() => {
      const payload = selected.payload
      const resolved = resolveFormat(payload, selected.hintedFormat)

      if (resolved === 'empty') {
        setPreparedPayload({ format: 'empty', display: '', error: null })
        setIsPreparing(false)
        return
      }

      if (resolved === 'json') {
        const { formatted, ok } = formatJson(payload)
        setPreparedPayload({
          format: ok ? 'json' : 'plain',
          display: formatted,
          error: ok ? null : 'Unable to parse payload as JSON. Showing raw text.',
        })
        setIsPreparing(false)
        return
      }

      if (resolved === 'form') {
        setPreparedPayload({ format: 'form', display: formatFormEncoded(payload), error: null })
        setIsPreparing(false)
        return
      }

      if (resolved === 'xml') {
        setPreparedPayload({ format: 'xml', display: formatXml(payload), error: null })
        setIsPreparing(false)
        return
      }

      setPreparedPayload({ format: resolved === 'unknown' ? 'plain' : resolved, display: payload, error: null })
      setIsPreparing(false)
    }, 0)

    return () => window.clearTimeout(job)
  }, [selected])

  const highlightable = useMemo(() => {
    if (!preparedPayload.display) return preparedPayload.display
    if (!searchTerm) return preparedPayload.display

    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    try {
      const regex = new RegExp(escaped, 'gi')
      const nodes: React.ReactNode[] = []
      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = regex.exec(preparedPayload.display)) !== null) {
        const matchIndex = match.index ?? 0
        if (matchIndex > lastIndex) {
          nodes.push(preparedPayload.display.slice(lastIndex, matchIndex))
        }

        nodes.push(
          <mark key={`highlight-${matchIndex}`} style={{ backgroundColor: '#ffe58a', color: '#1b1b1b' }}>
            {match[0]}
          </mark>
        )

        lastIndex = matchIndex + match[0].length
      }

      if (lastIndex < preparedPayload.display.length) {
        nodes.push(preparedPayload.display.slice(lastIndex))
      }

      return nodes.length ? nodes : preparedPayload.display
    } catch (_error) {
      return preparedPayload.display
    }
  }, [preparedPayload.display, preparedPayload.format, searchTerm])

  return (
    <>
      <table width="100%" className="table table--striped">
        <thead>
          <tr>
            <th>Service</th>
            <th>Request Type</th>
            <th>Request</th>
            <th>Response</th>
            <th>Format</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {apiResponses.map((response) => (
            <tr key={response.id}>
              <td>{response.service}</td>
              <td>{response.requestType}</td>
              <td>
                <button
                  type="button"
                  onClick={() =>
                    setSelected({
                      label: `${response.service} - ${response.requestType} request`,
                      payload: response.request,
                      hintedFormat: response.format,
                    })
                  }
                >
                  request
                </button>
              </td>
              <td>
                <button
                  type="button"
                  onClick={() =>
                    setSelected({
                      label: `${response.service} - ${response.requestType} response`,
                      payload: response.response,
                      hintedFormat: response.format,
                    })
                  }
                >
                  response
                </button>
              </td>
              <td>{formatLabel(resolveFormat(response.response, response.format))}</td>
              <td>{response.createdAt}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {selected && (
        <div style={{ border: '1px solid #ccc', marginTop: '10px' }}>
          <div style={{ backgroundColor: '#ddd', overflow: 'hidden' }}>
            <span
              style={{
                float: 'left',
                padding: '8px 12px',
                fontWeight: 600,
              }}
            >
              {selected.label} ({formatLabel(preparedPayload.format)})
            </span>
            <div style={{ textAlign: 'right' }}>
              <button
                type="button"
                onClick={async () => {
                  if (!selected?.payload) return
                  try {
                    if (navigator?.clipboard?.writeText) {
                      await navigator.clipboard.writeText(selected.payload)
                    }
                  } catch (_error) {
                    // Intentionally swallow clipboard errors
                  }
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '8px 12px',
                }}
              >
                <CopyIcon />
              </button>
              <button
                type="button"
                onClick={() => setSelected(null)}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '8px 12px',
                }}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
          {preparedPayload.format !== 'empty' && (
            <div
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#f9f9f9',
              }}
            >
              <label style={{ fontWeight: 600 }} htmlFor="api-response-search">
                Search
              </label>
              <input
                id="api-response-search"
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Find a key or value"
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
            </div>
          )}
          {preparedPayload.error && (
            <div
              style={{
                padding: '8px 12px',
                backgroundColor: '#fff5f5',
                color: '#8a1f1f',
                borderBottom: '1px solid #f5cccc',
              }}
            >
              {preparedPayload.error}
            </div>
          )}
          {isPreparing ? (
            <div
              style={{
                minHeight: '160px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              <Loader isLoading className="loader--button" />
              <span>Preparing payload…</span>
            </div>
          ) : (
            <pre
              style={{
                margin: 0,
                padding: '12px',
                maxHeight: '400px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                background: '#f5f5f5',
                color: '#1b1b1b',
              }}
            >
              {highlightable}
            </pre>
          )}
        </div>
      )}
    </>
  )
}
